import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, Briefcase, TrendingUp, CreditCard, Plus, Send, Trophy } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { MetricCard } from '../components/dashboard/MetricCard';
import { SalesChart } from '../components/dashboard/SalesChart';
import { ActivityFeed } from '../components/dashboard/ActivityFeed';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Avatar } from '../components/ui/Avatar';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { useToast } from '../components/ui/Toast';
import { activities, sellers } from '../utils/mockData';
export function Dashboard() {
  const navigate = useNavigate();
  const {
    addToast
  } = useToast();
  const topSellers = sellers.slice(0, 3);
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const [isDealModalOpen, setIsDealModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const handleCreateLead = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      setIsLeadModalOpen(false);
      addToast('Lead criado com sucesso!', 'success');
    }, 1000);
  };
  const handleCreateDeal = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      setIsDealModalOpen(false);
      addToast('Negócio criado com sucesso!', 'success');
    }, 1000);
  };
  return <Layout data-id="element-471">
      <div className="space-y-8 animate-in fade-in duration-500" data-id="element-472">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-473">
          <div data-id="element-474">
            <h1 className="text-3xl font-bold text-white" data-id="element-475">Dashboard</h1>
            <p className="text-text-muted" data-id="element-476">
              Bem-vindo de volta, Ana! Aqui está o resumo de hoje.
            </p>
          </div>
          <div className="flex gap-3" data-id="element-477">
            <Button variant="outline" icon={<Plus className="h-4 w-4" data-id="element-479" />} onClick={() => setIsLeadModalOpen(true)} data-id="element-478">
              Novo Lead
            </Button>
            <Button variant="primary" icon={<Plus className="h-4 w-4" data-id="element-481" />} onClick={() => setIsDealModalOpen(true)} data-id="element-480">
              Novo Negócio
            </Button>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-482">
          <MetricCard title="Vendas Totais" value="R$ 847.500" change="+12.5%" trend="up" color="primary" icon={<DollarSign className="h-6 w-6" data-id="element-484" />} data-id="element-483" />
          <MetricCard title="Negócios Ativos" value="23" change="+4" trend="up" color="secondary" icon={<Briefcase className="h-6 w-6" data-id="element-486" />} data-id="element-485" />
          <MetricCard title="Taxa de Conversão" value="68%" change="-2.1%" trend="down" color="accent" icon={<TrendingUp className="h-6 w-6" data-id="element-488" />} data-id="element-487" />
          <MetricCard title="Ticket Médio" value="R$ 12.450" change="+5.4%" trend="up" color="success" icon={<CreditCard className="h-6 w-6" data-id="element-490" />} data-id="element-489" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-491">
          {/* Main Chart */}
          <div className="lg:col-span-2 space-y-8" data-id="element-492">
            <SalesChart data-id="element-493" />

            {/* Quick Actions & Mini Ranking */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-494">
              <Card className="p-6" data-id="element-495">
                <h3 className="text-lg font-bold text-white mb-4" data-id="element-496">
                  Ações Rápidas
                </h3>
                <div className="grid grid-cols-2 gap-3" data-id="element-497">
                  <Button variant="outline" className="h-auto py-4 flex-col gap-2 hover:border-primary hover:text-primary transition-colors" onClick={() => navigate('/campanhas')} data-id="element-498">
                    <Send className="h-6 w-6" data-id="element-499" />
                    <span className="text-xs" data-id="element-500">Enviar Campanha</span>
                  </Button>
                  <Button variant="outline" className="h-auto py-4 flex-col gap-2 hover:border-secondary hover:text-secondary transition-colors" onClick={() => addToast('Relatório gerado e enviado por email!', 'info')} data-id="element-501">
                    <Briefcase className="h-6 w-6" data-id="element-502" />
                    <span className="text-xs" data-id="element-503">Gerar Relatório</span>
                  </Button>
                </div>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-card to-slate-800" data-id="element-504">
                <div className="flex items-center justify-between mb-4" data-id="element-505">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2" data-id="element-506">
                    <Trophy className="h-5 w-5 text-accent" data-id="element-507" />
                    Top Vendedores
                  </h3>
                  <span className="text-xs text-text-muted" data-id="element-508">Mês Atual</span>
                </div>
                <div className="space-y-4" data-id="element-509">
                  {topSellers.map((seller, idx) => <div key={seller.id} className="flex items-center justify-between" data-id="element-510">
                      <div className="flex items-center gap-3" data-id="element-511">
                        <div className={`
                          flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold
                          ${idx === 0 ? 'bg-accent text-white' : idx === 1 ? 'bg-slate-400 text-slate-900' : 'bg-orange-700 text-orange-100'}
                        `} data-id="element-512">
                          {idx + 1}
                        </div>
                        <Avatar src={seller.avatar} fallback={seller.name} size="sm" data-id="element-513" />
                        <span className="text-sm font-medium text-white" data-id="element-514">
                          {seller.name}
                        </span>
                      </div>
                      <span className="text-sm font-bold text-primary-light" data-id="element-515">
                        {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                      notation: 'compact'
                    }).format(seller.revenue)}
                      </span>
                    </div>)}
                </div>
              </Card>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="lg:col-span-1" data-id="element-516">
            <Card className="p-6 h-full" data-id="element-517">
              <ActivityFeed activities={activities} data-id="element-518" />
            </Card>
          </div>
        </div>
      </div>

      {/* New Lead Modal */}
      <Modal isOpen={isLeadModalOpen} onClose={() => setIsLeadModalOpen(false)} title="Novo Lead" footer={<>
            <Button variant="ghost" onClick={() => setIsLeadModalOpen(false)} data-id="element-520">
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleCreateLead} isLoading={isLoading} data-id="element-521">
              Salvar Lead
            </Button>
          </>} data-id="element-519">
        <form className="space-y-4" data-id="element-522">
          <Input label="Nome do Contato" placeholder="Ex: Maria Silva" data-id="element-523" />
          <Input label="Nome da Loja" placeholder="Ex: Maria Cosméticos" data-id="element-524" />
          <div className="grid grid-cols-2 gap-4" data-id="element-525">
            <Input label="Telefone/WhatsApp" placeholder="(00) 00000-0000" data-id="element-526" />
            <Input label="Cidade" placeholder="Ex: São Paulo" data-id="element-527" />
          </div>
          <Input label="Email" type="email" placeholder="maria@email.com" data-id="element-528" />
        </form>
      </Modal>

      {/* New Deal Modal */}
      <Modal isOpen={isDealModalOpen} onClose={() => setIsDealModalOpen(false)} title="Novo Negócio" footer={<>
            <Button variant="ghost" onClick={() => setIsDealModalOpen(false)} data-id="element-530">
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleCreateDeal} isLoading={isLoading} data-id="element-531">
              Criar Negócio
            </Button>
          </>} data-id="element-529">
        <form className="space-y-4" data-id="element-532">
          <Input label="Loja/Cliente" placeholder="Buscar cliente..." data-id="element-533" />
          <div className="grid grid-cols-2 gap-4" data-id="element-534">
            <Input label="Valor Estimado (R$)" placeholder="0,00" type="number" data-id="element-535" />
            <Select label="Etapa Inicial" options={[{
            value: 'novo',
            label: 'Novo Lead'
          }, {
            value: 'negociacao',
            label: 'Em Negociação'
          }, {
            value: 'proposta',
            label: 'Proposta Enviada'
          }]} data-id="element-536" />
          </div>
          <Select label="Vendedor Responsável" options={sellers.map(s => ({
          value: s.id,
          label: s.name
        }))} data-id="element-537" />
          <Input label="Observações" placeholder="Detalhes iniciais..." data-id="element-538" />
        </form>
      </Modal>
    </Layout>;
}